import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  FormControl,
  Select,
  makeStyles,
  Box,
  CircularProgress,
  IconButton,
} from '@material-ui/core';
import axios from 'axios';
import { appUrl } from '../../../../../../utils/config';
import CloseIcon from '@material-ui/icons/Close';
import WarningIcon from '@material-ui/icons/Warning';
import DeleteIcon from '@material-ui/icons/Delete';
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import jwtDecode from 'jwt-decode';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    fontWeight: 700,
    fontSize: '22px',
    marginBottom: '1.5rem',
    textAlign: 'center',
    color: '#000',
  },
  closeButton: {
    fontSize: '1rem',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: '#FF0000',
  },
  tableContainer: {
    boxShadow: "none",
  },
  table: {
    minWidth: 650,
  },
  tableCell: {
    textAlign: "left",
    backgroundColor: "#E5F2F7",
    borderBottom: "none",
    padding: "16px",
  },
  tableCell2: {
    textAlign: "left",
    backgroundColor: "#E5F2F7",
    borderBottom: "none",
    padding: "16px",
    paddingTop: "0px",
  },
  headerCell: {
    fontWeight: "bold",
  },
  submitButton: {
    backgroundColor: '#ec6535',
    color: '#fff',
    borderRadius: '20px',
    textTransform: 'none',
    padding: '8px 32px',
    '&:hover': {
      backgroundColor: '#d4572e',
    },
  },
  selectField: {
    minWidth: 110,
    maxWidth: 110,
    '& .MuiOutlinedInput-root': {
      height: '32px',
      '& .MuiOutlinedInput-input': {
        padding: '6px 14px',
      },
    },
  },
  aslTableContainer: {
    marginTop: '24px',
    boxShadow: "none",
    borderRadius: '8px',
    border: '1px solid #d0d0d0',
  },
  aslTableHeaderGrey: {
    fontWeight: "bold",
    backgroundColor: "#E0E0E0",
    textAlign: "left",
    padding: "16px",
    borderBottom: "1px solid #d0d0d0",
    '&:last-child': {
      borderRight: "none",
    },
  },
  aslTableCellWhite: {
    textAlign: "left",
    backgroundColor: "#FFFFFF",
    borderBottom: "1px solid #d0d0d0",
    padding: "12px 16px",
    '&:last-child': {
      borderRight: "none",
    },
  },
  aslTitle: {
    fontWeight: 700,
    fontSize: '18px',
    marginTop: '24px',
    marginBottom: '16px',
    color: '#000',
  },
  fieldLabel: {
    fontWeight: 600,
    fontSize: '14px',
    color: '#333',
    marginBottom: '4px',
  },
  borders: {
    border: '2px solid #707070',
    display: 'flex',
    width: '1.6rem',
    backgroundColor: 'grey',
  },
}));

const CreateIBDDialog = ({ open, onClose, batchData, onSuccess }) => {
  const classes = useStyles();
  const [shippingAllQuantity, setShippingAllQuantity] = useState('Yes');
  const [shipmentRows, setShipmentRows] = useState([{ id: 1, quantity: '' }]);
  const [readyForDispatchQty, setReadyForDispatchQty] = useState(0);
  const [uom, setUom] = useState('');
  const [quantitySumError, setQuantitySumError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingBatchDetails, setLoadingBatchDetails] = useState(false);
  const [batchDetailsData, setBatchDetailsData] = useState(null);
  const [qtyAlreadyDispatched, setQtyAlreadyDispatched] = useState(0);
  const [lastShippedDate, setLastShippedDate] = useState('');
  const [remainingQty, setRemainingQty] = useState(0);
  const [isPartialIBD, setIsPartialIBD] = useState(false);
 const [maxPostingQtyAllowed, setMaxPostingQtyAllowed] = useState(null);
  const [maxPostingQtyUom, setMaxPostingQtyUom] = useState('');
  // Get user information from token
  const getUserFromToken = () => {
    const token = localStorage.getItem('authToken');
    if (!token) return null;
    
    try {
      const decoded = jwtDecode(token);
      return {
        fullName: decoded.full_name || decoded.fullName || 'N/A',
      };
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  };

  const userInfo = getUserFromToken();

  // Validate shipment quantities sum
  const validateShipmentQuantities = () => {
    // Only include rows with valid non-empty values (avoids || 0 coercion)
    const shipmentQuantities = shipmentRows
      .map(row => parseFloat(row.quantity))
      .filter(qty => !isNaN(qty));

    // Check if any user-typed row has value 0 (skip programmatically set numbers)
    const hasZeroValue = shipmentRows.some(row => {
      if (typeof row.quantity !== 'string') return false;
      if (row.quantity.trim() === '') return false;
      const qty = parseFloat(row.quantity);
      return !isNaN(qty) && qty === 0;
    });

    if (hasZeroValue) {
      setQuantitySumError('You have entered 0 value. Please enter a valid Individual Shipment Size.');
      return false;
    }

    const totalShipmentQty = shipmentQuantities.reduce((sum, qty) => sum + qty, 0);
    const qtyToValidate = isPartialIBD ? remainingQty : readyForDispatchQty;
    const isSingleRow = shipmentRows.length === 1;

    // 1st: Validate against readyForDispatchQty / remainingQty
    if (shippingAllQuantity === 'Yes') {
      if (totalShipmentQty !== qtyToValidate) {
        setQuantitySumError(isSingleRow
          ? `Shipment Size is not matching ${isPartialIBD ? 'Remaining Qty.' : 'Ready for Dispatch Qty.'}`
          : `Sum of Individual Shipment Size is not matching ${isPartialIBD ? 'Remaining Qty.' : 'Ready for Dispatch Qty.'}`);
        return false;
      }
    } else {
      if (totalShipmentQty > qtyToValidate) {
        setQuantitySumError(isSingleRow
          ? `Shipment Size cannot exceed ${isPartialIBD ? 'Remaining Qty.' : 'Ready for Dispatch Qty.'}`
          : `Sum of Individual Shipment Size cannot exceed ${isPartialIBD ? 'Remaining Qty.' : 'Ready for Dispatch Qty.'}`);
        return false;
      }
    }

    // 2nd: Validate each row against maxPostingQtyAllowed (only when > 0)
    if (maxPostingQtyAllowed !== null && maxPostingQtyAllowed !== undefined && maxPostingQtyAllowed > 0) {
      const exceedsMax = shipmentRows.some((row) => {
        const qty = parseFloat(row.quantity);
        return !isNaN(qty) && qty > maxPostingQtyAllowed;
      });
      if (exceedsMax) {
        setQuantitySumError(
          `Individual Shipment Size cannot exceed the maximum posting quantity of ${Number(maxPostingQtyAllowed).toLocaleString("en-US")} ${maxPostingQtyUom}.`
        );
        return false;
      }
    }

    setQuantitySumError('');
    return true;
  };

  // Update validation whenever rows change
  useEffect(() => {
    const qtyToCheck = isPartialIBD ? remainingQty : readyForDispatchQty;
    const hasAnyValue = shipmentRows.some(row => row.quantity !== '' && row.quantity !== null && row.quantity !== undefined);
    
    if (qtyToCheck > 0 && shipmentRows.length > 0 && hasAnyValue) {
      validateShipmentQuantities();
    } else if (!hasAnyValue) {
      // Clear error if all fields are empty
      setQuantitySumError('');
    }
  }, [shipmentRows, readyForDispatchQty, remainingQty, isPartialIBD, shippingAllQuantity]);

  // Fetch batch details
  const fetchBatchDetails = async () => {
    if (!batchData?.schrockenDeliveryScheduleBatchID) {
      console.error("schrockenDeliveryScheduleBatchID is required");
      return;
    }

    setLoadingBatchDetails(true);
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchDeliveryScheduleBatchDetails`;
      const payload = {
        schrockenDeliveryScheduleBatchID: batchData.schrockenDeliveryScheduleBatchID,
        poNumber: batchData.poNumber,
        batchNumber: batchData.batchNumber,
      };

      const response = await axios.post(url, payload);
      
      if (response.data.status) {
        const data = response.data.data;
        setBatchDetailsData(data);

        // Set ready for dispatch quantity and UOM
        setReadyForDispatchQty(data.readyForDispatchQuantity || 0);
        setUom(data.batchUOM || '');

        // Set maxPostingQtyAllowed from fetchDeliveryScheduleBatchDetails only when isActive=true
        if (data?.isActive === true && data?.maxPostingQtyAllowed !== undefined && data?.maxPostingQtyAllowed !== null) {
          setMaxPostingQtyAllowed(data.maxPostingQtyAllowed);
          setMaxPostingQtyUom(data.targetQuantityUOM || data.batchUOM || '');
        }
        
        // Initialize first shipment row - don't auto-fill for partial IBD
        // For non-partial IBD with 'No' shipping all (default), keep empty
        // For partial IBD, shipment rows are already set in useEffect based on shippingAllQuantity
        // Don't override them here
        
        return data;
      } else {
        console.error("API returned error status:", response.data);
        return null;
      }
    } catch (error) {
      console.error("Error fetching batch details:", error);
      return null;
    } finally {
      setLoadingBatchDetails(false);
    }
  };

  useEffect(() => {
    if (open && batchData?.schrockenDeliveryScheduleBatchID) {
      
      // Check if batch status is "Partial IBD Generated"
      const isPartial = batchData?.statusDisplayName === 'Partial IBD Generated' || 
                        batchData?.SASLBatchStatusID === 61;
      setIsPartialIBD(isPartial);
      setShippingAllQuantity(isPartial ? 'No' : 'Yes');
      setShipmentRows([{ id: 1, quantity: '' }]);

      
      // Set partial IBD data from batchData (dashboard API response) only if it's partial
      if (isPartial) {
        if (batchData?.qtyAlreadyDispatched) {
          setQtyAlreadyDispatched(batchData.qtyAlreadyDispatched);
        }
        if (batchData?.lastShippedDate) {
          setLastShippedDate(batchData.lastShippedDate);
        }
        if (batchData?.remainingQtyTobeShipped) {
          setRemainingQty(batchData.remainingQtyTobeShipped);
        }
      } else {
        // Reset partial fields if not partial status
        setQtyAlreadyDispatched(0);
        setLastShippedDate('');
        setRemainingQty(0);
      }
      
      fetchBatchDetails();
    }
  }, [open, batchData?.schrockenDeliveryScheduleBatchID]);

  // Helper function to derive user-friendly error type from error message
  const deriveErrorType = (errorMessage) => {
    if (!errorMessage) return '';
    
    const lowerMessage = errorMessage.toLowerCase();
    
    // Check for IBD not created errors
    if (lowerMessage.includes('ibd not created') || lowerMessage.includes('i:ibd not created')) {
      return 'Generic IBD not created';
    }
    
    // Check for material not found errors
    if (lowerMessage.includes('material') && lowerMessage.includes('not found')) {
      return 'Material not found in BOM';
    }
    
    return '';
  };

  const displayData = batchDetailsData ? {
    cmo: batchDetailsData.cmoMember || batchDetailsData.vendorName || 'N/A',
    cmoIdFromSponsorERP: batchDetailsData.vendorNumber || 'N/A',
    plantId: batchDetailsData.vendorPlant || 'N/A',
    poNumber: batchData?.poNumber || 'N/A',
    productCode: batchDetailsData.fgMaterialCode || batchDetailsData.productCode || 'N/A',
    productDescription: batchDetailsData.fgMaterialName || batchDetailsData.productDescription || 'N/A',
    batchNumber: batchData?.batchNumber || 'N/A',
    dateOfManufacturing: batchDetailsData.manufacturingDate ? new Date(batchDetailsData.manufacturingDate).toLocaleDateString('en-GB') : 'N/A',
    productExpiryDate: batchDetailsData.expiryDate ? new Date(batchDetailsData.expiryDate).toLocaleDateString('en-GB') : 'N/A',
    loggedInUser: userInfo?.fullName || 'N/A',
  } : {
    cmo: 'Loading...',
    cmoIdFromSponsorERP: 'Loading...',
    plantId: 'Loading...',
    poNumber: batchData?.poNumber || 'Loading...',
    productCode: 'Loading...',
    productDescription: 'Loading...',
    batchNumber: batchData?.batchNumber || 'Loading...',
    dateOfManufacturing: 'Loading...',
    productExpiryDate: 'Loading...',
    loggedInUser: userInfo?.fullName || 'Loading...',
  };

  const handleSubmit = async () => {
    // Validate shipment quantities
    if (!validateShipmentQuantities()) {
      console.error('Sum of shipment quantities must equal ready for dispatch quantity');
      return;
    }

    const shipmentQuantities = shipmentRows.map(row => parseFloat(row.quantity) || 0).filter(q => q > 0);
    
    if (shipmentQuantities.length === 0) {
      console.error('Please enter at least one valid shipment quantity');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/generateIBDWithConsumptionFromFE`;
      
      const payload = {
        schrockenDeliveryScheduleBatchID: batchData?.schrockenDeliveryScheduleBatchID,
        poNumber: displayData.poNumber,
        batchNumber: displayData.batchNumber,
        allQuantitiesShipped: shippingAllQuantity === 'Yes',
        shipmentQuantities: shipmentQuantities,
      };


      const response = await axios.post(url, payload);
      
      if (response.data.success) {
        if (onSuccess) {
          onSuccess({
            success: true,
            message: 'IBD created successfully'
          });
        }
        onClose();
      } else {
        console.error("IBD creation failed:", response.data);
        
        // Extract error details from response
        let errorType = '';
        let errorMessage = '';
        
        // Check for new response structure: response.data.sapResponse (array)
        if (response.data.sapResponse && Array.isArray(response.data.sapResponse) && response.data.sapResponse.length > 0) {
          const sapResponse = response.data.sapResponse[0];
          
          // Get error message from sapResponse Output
          if (sapResponse.Output && sapResponse.Output.item && sapResponse.Output.item.length > 0) {
            errorMessage = sapResponse.Output.item[0].ErrorMsg || '';
          }
          
          // Derive user-friendly error type
          errorType = deriveErrorType(errorMessage);
          
          // If no derived error type and no message, use fallback
          if (!errorType && !errorMessage) {
            errorMessage = 'IBD not created Bad Request : 400 : HTTP/1.1';
            errorType = 'Generic IBD not created';
          }
        }
        // Fallback to old structure: response.data.data.failedShipments
        else if (response.data.data && response.data.data.failedShipments && response.data.data.failedShipments.length > 0) {
          const failedShipment = response.data.data.failedShipments[0];
          
          // Get error message from sapResponse
          if (failedShipment.sapResponse && failedShipment.sapResponse.Output && failedShipment.sapResponse.Output.item && failedShipment.sapResponse.Output.item.length > 0) {
            errorMessage = failedShipment.sapResponse.Output.item[0].ErrorMsg || '';
          }
          
          // Derive user-friendly error type
          errorType = deriveErrorType(errorMessage);
          
          // If no derived error type and no message, use fallback
          if (!errorType && !errorMessage) {
            errorMessage = 'IBD not created Bad Request : 400 : HTTP/1.1';
            errorType = 'Generic IBD not created';
          }
        }
        
        if (onSuccess) {
          onSuccess({ 
            success: false, 
            message: 'IBD creation failed',
            errorType: errorType,
            errorMessage: errorMessage
          });
        }
      }
      
    } catch (error) {
      console.error("Error creating IBD:", error);
      
      let errorType = '';
      let errorMessage = '';
      
      if (error.response && error.response.data) {
        // Extract error details from error response
        // Check for new response structure: error.response.data.sapResponse (array)
        if (error.response.data.sapResponse && Array.isArray(error.response.data.sapResponse) && error.response.data.sapResponse.length > 0) {
          const sapResponse = error.response.data.sapResponse[0];
          
          // Get error message from sapResponse Output
          if (sapResponse.Output && sapResponse.Output.item && sapResponse.Output.item.length > 0) {
            errorMessage = sapResponse.Output.item[0].ErrorMsg || '';
          }
          
          // Derive user-friendly error type
          errorType = deriveErrorType(errorMessage);
        }
        // Fallback to old structure: error.response.data.data.failedShipments
        else if (error.response.data.data && error.response.data.data.failedShipments && error.response.data.data.failedShipments.length > 0) {
          const failedShipment = error.response.data.data.failedShipments[0];
          
          // Get error message from sapResponse
          if (failedShipment.sapResponse && failedShipment.sapResponse.Output && failedShipment.sapResponse.Output.item && failedShipment.sapResponse.Output.item.length > 0) {
            errorMessage = failedShipment.sapResponse.Output.item[0].ErrorMsg || '';
          }
          
          // Derive user-friendly error type
          errorType = deriveErrorType(errorMessage);
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
          errorType = deriveErrorType(errorMessage);
        }
        
        // If no derived error type and no message, use fallback based on status code
        if (!errorType && !errorMessage) {
          const statusCode = error.response.status;
          errorMessage = `IBD not created Bad Request : ${statusCode} : HTTP/1.1`;
          errorType = 'Generic IBD not created';
        }
      }
      
      if (onSuccess) {
        onSuccess({
          success: false,
          message: 'IBD creation failed',
          errorType: errorType,
          errorMessage: errorMessage
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        onClose();
      }}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 3,
          padding: 3,
          backgroundColor: "#fafafa",
          position: 'relative',
        },
      }}
      style={{ maxHeight: '100vh'}}
    >
      {/* Loading Overlay for Batch Details or Submission - Covers entire dialog */}
      {(loadingBatchDetails || isSubmitting) && (
        <Box 
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex" 
          alignItems="center" 
          justifyContent="center" 
          style={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '24px',
            zIndex: 2000
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="center">
            <CircularProgress size={40} style={{ marginBottom: '16px' }} />
            <Typography>{isSubmitting ? 'Creating IBD...' : ''}</Typography>
          </Box>
        </Box>
      )}
      
      <DialogContent sx={{ overflowY: "auto" }} style={{ padding: '24px 40px 32px 40px' }}>
        
        <div style={{ position: 'relative' }}>
          <Grid container justifyContent="flex-end"  style={{ position: 'absolute', right: "0px", top: "0px"}}>
          <Typography
            className={classes.closeButton}
            variant="body2"
            onClick={onClose}
          >
            Cancel <CloseIcon style={{ marginLeft: '4px' }} fontSize="large" />
          </Typography>
        </Grid>

        <Typography className={classes.dialogTitle}>
          Creating IBD
        </Typography>
        </div>

        {/* Batch Information Table */}
        <Grid
          container
          justifyContent="flex-start"
          style={{ backgroundColor: "#c2e1ef", marginTop: '24px', marginBottom: '24px' }}
        >
          <Grid item>
            <TableContainer
              component={Paper}
              className={classes.tableContainer}
            >
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell className={`${classes.tableCell} ${classes.headerCell}`}>
                      CMO
                    </TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.headerCell}`}>
                      CMO ID from BMS ERP
                    </TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.headerCell}`}>
                      Plant ID
                    </TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.headerCell}`}>
                      PO Number
                    </TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.headerCell}`}>
                      Product Code / Description
                    </TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.headerCell}`}>
                      Batch Number
                    </TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.headerCell}`}>
                      Date of Manufacturing
                    </TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.headerCell}`}>
                      Product Expiry Date
                    </TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.headerCell}`}>
                      Logged in user
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell className={classes.tableCell2}>{displayData.cmo}</TableCell>
                    <TableCell className={classes.tableCell2}>{displayData.cmoIdFromSponsorERP}</TableCell>
                    <TableCell className={classes.tableCell2}>{displayData.plantId}</TableCell>
                    <TableCell className={classes.tableCell2}>{displayData.poNumber}</TableCell>
                    <TableCell className={classes.tableCell2}>
                      {displayData.productCode} / {displayData.productDescription}
                    </TableCell>
                    <TableCell className={classes.tableCell2}>{displayData.batchNumber}</TableCell>
                    <TableCell className={classes.tableCell2}>{displayData.dateOfManufacturing}</TableCell>
                    <TableCell className={classes.tableCell2}>{displayData.productExpiryDate}</TableCell>
                    <TableCell className={classes.tableCell2}>{displayData.loggedInUser}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>

        {/* IBD Details Section */}
        <Typography className={classes.aslTitle} style={{ marginTop: '32px' }}>
          IBD Details
          <span className={classes.borders}></span>
        </Typography>
        
        <Grid container spacing={3} style={{ marginTop: '16px', marginBottom: '24px' }}>
          <Grid item xs={12} md={2}>
            <Typography className={classes.fieldLabel} style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
              Ready for Dispatch Qty.
            </Typography>
            <Typography style={{ fontSize: '16px', color: '#333' }}>
            {(readyForDispatchQty !== null && readyForDispatchQty !== undefined && readyForDispatchQty !== '' && Number.isFinite(Number(readyForDispatchQty))) ? Number(readyForDispatchQty).toFixed(3) : 'N/A'}
            </Typography>
          </Grid>
          
          {/* Partial IBD Info - Show when batch has been partially dispatched */}
          {isPartialIBD && qtyAlreadyDispatched > 0 && (
            <>
              <Grid item xs={12} md={2}>
                <Typography className={classes.fieldLabel} style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                  Qty. already dispatched
                </Typography>
                <Typography style={{ fontSize: '16px', color: '#333' }}>
                {(qtyAlreadyDispatched !== null && qtyAlreadyDispatched !== undefined && qtyAlreadyDispatched !== '' && Number.isFinite(Number(qtyAlreadyDispatched))) ? Number(qtyAlreadyDispatched).toFixed(3) : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography className={classes.fieldLabel} style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                  Last shipped at
                </Typography>
                <Typography style={{ fontSize: '16px', color: '#333' }}>
                  {lastShippedDate ? new Date(lastShippedDate).toLocaleString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false
                  }).replace(/,/g, '').toUpperCase() : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography className={classes.fieldLabel} style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                  Remaining Qty.
                </Typography>
                <Typography style={{ fontSize: '16px', color: '#333' }}>
                {(remainingQty !== null && remainingQty !== undefined && remainingQty !== '' && Number.isFinite(Number(remainingQty))) ? Number(remainingQty).toFixed(3) : 'N/A'}
                </Typography>
              </Grid>
            </>
          )}
          
          <Grid item xs={12} md={1}>
            <Typography className={classes.fieldLabel} style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
              UoM
            </Typography>
            <Typography style={{ fontSize: '16px', color: '#333' }}>
              {uom}
            </Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography className={classes.fieldLabel} style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
              Shipping all {isPartialIBD ? 'remaining' : ''} quantity
            </Typography>
            <FormControl variant="outlined" size="small" fullWidth className={classes.selectField}>
              <Select
                value={shippingAllQuantity}
                onChange={(e) => {
                  setShippingAllQuantity(e.target.value);
                  if (e.target.value === 'Yes') {
                    // For partial IBD, use remainingQty; otherwise use readyForDispatchQty
                    const qtyToUse = isPartialIBD ? remainingQty : readyForDispatchQty;
                    setShipmentRows([{ id: 1, quantity: qtyToUse || '' }]);
                  } else {
                    setShipmentRows([{ id: 1, quantity: '' }]);
                  }
                }}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Shipment Sizes Table */}
        <Grid container style={{  marginBottom: '24px' }}>
          <Grid item xs={12}>
            <TableContainer style={{maxWidth: "660px", marginTop: 0}} component={Paper} className={classes.aslTableContainer}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.aslTableHeaderGrey}>IBD Serial Number</TableCell>
                    <TableCell className={classes.aslTableHeaderGrey}>Individual Shipment Size</TableCell>
                    <TableCell className={classes.aslTableHeaderGrey}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shipmentRows.map((row, index) => (
                    <TableRow key={row.id}>
                      <TableCell className={classes.aslTableCellWhite}>{index + 1}</TableCell>
                      <TableCell className={classes.aslTableCellWhite}>
                        <TextField
                          type="number"
                          variant="outlined"
                          size="small"
                          value={row.quantity}
                          onChange={(e) => {
                            const newRows = [...shipmentRows];
                            newRows[index].quantity = e.target.value;
                            setShipmentRows(newRows);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === '+' || e.key === '-' || e.key === 'e' || e.key === 'E') {
                              e.preventDefault();
                            }
                          }}
                          fullWidth
                          inputProps={{ min: 0, step: "any" }}
                        />
                      </TableCell>
                      <TableCell className={classes.aslTableCellWhite}>
                        {index > 0 && (
                          <IconButton
                            onClick={() => {
                              setShipmentRows(shipmentRows.filter(r => r.id !== row.id));
                            }}
                            size="small"
                          >
                            <DeleteIcon color="error" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>

        {/* Notes */}
        <Grid container style={{ marginBottom: '16px' }}>
          <Grid item xs={12}>
            <Typography variant="body2" style={{ fontSize: '12px', color: '#666' }}>
              Note 1 - If Shipping all {`${isPartialIBD ? 'remaining' : ''}`} quantity = YES, sum of all Individual Shipment Size should match {`${isPartialIBD ? 'Remaining Qty.' : 'Ready for Dispatch Qty.'}`}
            </Typography>
            <Typography variant="body2" style={{ fontSize: '12px', color: '#666' }}>
              Note 2 - Consumed batch quantity is assigned proportionately to each IBD Serial Number, based on the Individual Shipment Size.
            </Typography>
             {maxPostingQtyAllowed !== null && maxPostingQtyAllowed !== undefined && maxPostingQtyAllowed > 0 && (
              <Typography variant="body2" style={{ fontSize: '12px', color: '#666' }}>
                Note 3 - Under shipment configuration, this Product is allowed maximum posting quantity of {Number(maxPostingQtyAllowed).toLocaleString("en-US")} {maxPostingQtyUom} only.
              </Typography>
            )}
          </Grid>
        </Grid>

        {/* Add New Row Button */}
        <Grid container style={{ marginBottom: '24px' }}>
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                const newId = Math.max(...shipmentRows.map(r => r.id), 0) + 1;
                setShipmentRows([...shipmentRows, { id: newId, quantity: '' }]);
              }}
              startIcon={<AddCircleOutlineIcon style={{ fontSize: 21 }} />}
              style={{
                fontSize: "14px",
                minWidth: "162px",
                height: "40px",
                padding: "5px 12px",
                borderColor: "#000000",
                color: "#000000",
                fontWeight: "600",
                borderRadius: "20px"
              }}
            >
              Add New Row
            </Button>
          </Grid>
        </Grid>

        {/* Validation Error */}
        {quantitySumError && (
          <Box style={{
            border: '1px solid #f44336',
            backgroundColor: '#ffebee',
            color: '#d32f2f',
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '24px',
          }}>
            <WarningIcon style={{ color: '#f44336', marginRight: '12px', fontSize: '24px' }} />
            <Typography style={{ fontSize: '14px', fontWeight: 500 }}>
              {quantitySumError}
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions style={{ justifyContent: 'center', paddingTop: '24px', paddingBottom: '24px', paddingLeft: '40px', paddingRight: '40px' }}>
        <Button
          variant="contained"
          className={classes.submitButton}
          onClick={handleSubmit}
          disabled={
            isSubmitting || 
            !!quantitySumError || 
            !shipmentRows.every(row => parseFloat(row.quantity) > 0)
          }
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isSubmitting ? 'Creating IBD...' : 'Confirm Submission'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateIBDDialog;
